import React, { useEffect, useRef, useState } from "react";
import { withRouter } from "next/router";
import { WithRouterProps } from "next/dist/client/with-router";
import Link from "next/link";

import Logo from "../../../../public/images/logo-new.svg";
import { emailValidation } from "../../../utils/regex";
import { LoginInterface } from "../../../models/Login";

import {
  CustomSubmitButton,
  BasicInfoContainer as LoginContainer,
  ImageContainer,
  Title,
  FieldsContainer,
  ForgottenPasswordLink,
  StyledLink,
  AlreadyMemberButton as SignUpButton,
} from "../../Register/BasicInfo/BasicInfo.styles";

import { InfoMessage, TextField } from "../..";
import { CTAForthContainer } from "../../../pages/Register/Register.styles";
import { useAuthContext } from "src/contexts/AuthContext";
import { extractErrorMessageFromProblemDetails } from "src/utils/string-utils";

interface LoginFormProps extends WithRouterProps {
  redirectURL?: string;
}

const LoginForm: React.FC<LoginFormProps> = ({
  router,
  redirectURL,
}) => {
  const [loginInProgress, setLoginInProgress] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [emailError, setEmailError] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const { signIn, user } = useAuthContext();

  const [pendingRedirect, setPendingRedirect] = useState<string | null>(null);

  useEffect(() => {
    if (pendingRedirect && user) {
      router.push(pendingRedirect);
    }
  }, [pendingRedirect, user]);

  let loginData: LoginInterface = { email: "" };

  const handleLogin = () => {
    if (!emailValidation(email)) {
      setEmailError(true);
      setLoginError(true);
      setErrorMessage("Oops! Your email appears to be in the wrong format.");
      return;
    } else {
      setLoginInProgress(true);
      loginData = { email: email, password: password };

      signIn(loginData).then(async (response) => {
        if (response.data) {
          if (redirectURL) {
            setPendingRedirect(redirectURL);
          } else if (response.data.user?.roles[0] === "User") {
            setPendingRedirect("/profile");
          } else {
            setPendingRedirect("/");
          }
        } else {
          if (typeof response.error === "string") {
            setErrorMessage(response.error);
          } else {
            setErrorMessage(extractErrorMessageFromProblemDetails(response.error!));
          }
          setLoginError(true);
          setLoginInProgress(false);
        }
      });
    }
  };

  const handleForgotPassword = () => {
    router.push("/reset-password");
  };

  const isSubmitButtonDisabled = () => {
    return Boolean(!email || !password);
  };

  return (
    <LoginContainer>
      <ImageContainer>
        <Logo />
      </ImageContainer>
      <Title>Welcome Back!</Title>
      <FieldsContainer>
        <TextField
          aria-label="Email Address"
          data-cy="login-email"
          type="email"
          id="email"
          placeholder="Email"
          size="lg"
          autoComplete="username"
          value={email}
          error={emailError}
          disabled={loginInProgress}
          fullWidth
          autoFocus
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setEmail(event.target.value);
            if (emailError) {
              if (!emailValidation(event.target.value)) {
                setEmailError(true);
              } else {
                setEmailError(false);
                setLoginError(false);
              }
            }
          }}
          InputProps={{ spellCheck: "false", autoCapitalize: "none" }}
        />
        <TextField
          aria-label="Password"
          data-cy="login-password"
          id="password"
          type="password"
          size="lg"
          placeholder="Password"
          autoComplete="current-password"
          value={password}
          disabled={loginInProgress}
          fullWidth
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setPassword(event.target.value);
          }}
          InputProps={{ spellCheck: "false", autoCapitalize: "none" }}
        />

        {loginError && (
          <InfoMessage
            variant="error"
            hasBackground={true}
            message={errorMessage}
          />
        )}
        <ForgottenPasswordLink>
          <StyledLink href="/reset-password">
            Forgotten Password
          </StyledLink>
        </ForgottenPasswordLink>
      </FieldsContainer>
      <CTAForthContainer>
        <CustomSubmitButton
          data-cy="login-button"
          disabled={isSubmitButtonDisabled()}
          disabledStateStyling="lowOpacity"
          loading={loginInProgress}
          onClick={() => handleLogin()}
          full
        >
          Login
        </CustomSubmitButton>
      </CTAForthContainer>
      <SignUpButton>
        Not a Member Yet?{" "}
        <StyledLink href="/register">
          Sign Up
        </StyledLink>
      </SignUpButton>
    </LoginContainer>
  );
};

LoginForm.displayName = "LoginForm";

const ComponentWithRouter = withRouter(LoginForm);

export { ComponentWithRouter as LoginForm };
